<template>
    <div class="flex ai:center gap:8 username">
        <member-avatar :icon-url="userInfo.avatar_url" :short-name="userInfo.short_name" :type="userInfo.type"></member-avatar>
        <div class="ai:center gap:8 flex:1 d:none">
            <span v-text="nickname"></span>
            <button class="w:20 h:20 bg:url(image/btn_change_username.svg) b:0 inline-block" @click="emit('chuser')" type="button" v-if="!item"></button>
        </div>
    </div>
    <div class="attachments d:none" v-if="image">
        <div class="rel inline-flex mr:24">
            <img alt="" class="max-h:72 max-w:100% r:8" :src="image.data || `files/${image}`" />
            <button class="bg:url(image/icon_round_close.svg) w:24 h:24 b:0 background-size:24|24 abs top:0 right:0 mr:-32" @click="image = null" type="button"></button>
        </div>
    </div>
    <div class="attachments d:none" v-if="gif">
        <div class="rel inline-flex mr:24">
            <img alt="" class="max-h:72 max-w:100% r:8" :src="`https://i.giphy.com/${gif}.gif`" />
            <button class="bg:url(image/icon_round_close.svg) w:24 h:24 b:0 background-size:24|24 abs top:0 right:0 mr:-32" @click="gif = null" type="button"></button>
        </div>
    </div>
    <div class="attachments d:none" v-if="yt">
        <div class="rel inline-flex mr:24">
            <div class="{max-h:72;max-w:100%;r:8}>div">
                <yt-player :content="yt"></yt-player>
            </div>
            <button class="bg:url(image/icon_round_close.svg) w:24 h:24 b:0 background-size:24|24 abs top:0 right:0 mr:-32" @click="yt = null" type="button"></button>
        </div>
    </div>
    <div class="word-break:break-all" @click.stop="focus">
        <template v-if="item?.target_id">
            <span class="color:primary" v-if="item.parent_id !== item.target_id">B{{ item.parent_floor }}-{{ item.target_floor }}&nbsp;</span>
            <span class="color:primary" v-else>B{{ item.target_floor }}&nbsp;</span>
        </template>
        <span class="color:primary" v-if="to">
            B<span v-if="to.parent_floor">{{ to.parent_floor }}-</span>{{ to.floor }}&nbsp;
        </span>
        <span
            aria-placeholder="你在想什麼？"
            class="f:16 lh:1.5 flex:1 outline:none {content:attr(aria-placeholder);color:#A9A9A9;z:-1}:empty::after inline-block:empty"
            contenteditable
            @focus="focus"
            @input="sync"
            @paste.prevent="paste"
            ref="replyInput"
        ></span>
    </div>
    <div class="buttons mt:4 jc:space-between ai:center d:none">
        <div class="inline-flex gap:16 ai:center">
            <div class="inline-flex">
                <div class="abs z:150 mt:-9" :class="smileyClass" v-show="smiley?.opened">
                    <comment-smiley-selector :attachment="hasAttachment" ref="smiley" @select="select"></comment-smiley-selector>
                </div>
                <button class="bg:url(image/icon_smile.svg) w:19 h:18 b:0" @click="smiley.toggle()" ref="smileyButton" type="button"></button>
            </div>
            <button class="bg:url(image/icon_image_upload.svg) w:19 h:18 b:0" @click="openImagePicker" type="button" v-if="!hasAttachment"></button>
            <input accept="image/*" @change="pickImage" class="hidden" ref="imageInput" type="file" />
        </div>
        <div class="flex ai:center gap:12">
            <button class="p:0 b:0 color:#A9A9A9" @click="cancel" type="button">取消</button>
            <button class="p:0 b:0 bg:url(image/btn_comment_submit.svg) w:18 h:18 inline-block" @click="update" type="button" v-if="submittable"></button>
        </div>
    </div>
</template>

<script setup>
    const { useParentElement, useWindowSize } = VueUse;

    const userInfo = useUserInfo();

    const props = defineProps({
        fixed: { default: false, type: Boolean },
        item: { type: Object },
        to: { type: Object }
    });

    const content = ref();
    const gif = ref();
    const image = ref();
    const imageInput = ref();
    const replyInput = ref();
    const smiley = ref();
    const smileyButton = ref();
    const smileyClass = ref({});
    const { width } = useWindowSize();
    const yt = ref();

    const hasAttachment = computed(() => {
        return image.value || gif.value || yt.value;
    });

    const nickname = computed(() => {
        switch (type.value) {
            case 1:
                return userInfo.college_name;
            case 2:
                return userInfo.college2_name;
            case 3:
                return userInfo.short_name;
            case 4:
                return userInfo.nickname;
            case 5:
                return userInfo.college3_name;
        }
    });

    const submittable = computed(() => {
        if (image.value != props.item?.image || gif.value != props.item?.gif_id || yt.value?.id != props.item?.yt_id) {
            return true;
        }

        return content.value && content.value != props.item?.content;
    });

    const type = computed(() => {
        return props.item?.nickname_type || userInfo.type;
    });

    const cancel = () => {
        emit("cancel");
        init();
    }

    const emit = defineEmits(["cancel", "chuser", "focus", "update"]);

    const focus = () => {
        replyInput.value.focus();
        emit("focus");
    };

    const init = () => {
        replyInput.value.textContent = props.item?.content;
        image.value = props.item?.image;
        gif.value = props.item?.gif_id;
        yt.value = props.item?.yt;
        sync();
    };

    const openImagePicker = () => {
        imageInput.value.click();
    };

    const paste = (event) => {
        let text = "";

        if (event.clipboardData) {
            text = (event.originalEvent || event).clipboardData.getData("text/plain");
        } else if (window.clipboardData) {
            text = window.clipboardData.getData("Text");
        }

        if (document.queryCommandSupported("insertText")) {
            document.execCommand("insertText", false, text);
        } else {
            const range = document.getSelection().getRangeAt(0);
            range.deleteContents();

            const textNode = document.createTextNode(text);
            range.insertNode(textNode);
            range.selectNodeContents(textNode);
            range.collapse(false);

            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }

        sync();
    };

    const pickImage = () => {
        const files = imageInput.value.files;

        if (files && files.length) {
            const file = files[0];
            const reader = new FileReader();

            reader.onload = () => {
                image.value = {
                    name: file.name,
                    data: reader.result
                };
            };

            reader.readAsDataURL(file);
        }

        imageInput.value.value = "";
    };

    const select = (type, item) => {
        switch (type) {
            case "emoji":
                replyInput.value.textContent += item.code;
                sync();
                break;
            case "gif":
                gif.value = item.id;
                api("api/comment/gif/use", { id: item.id });
                break;
            case "yt":
                yt.value = item;
                api("api/comment/yt/use", { id: item.id });
                break;
        }

        smiley.value.close();
    };

    const sync = () => {
        content.value = replyInput.value.textContent.trim();
    };

    const update = () => {
        emit("update", {
            image: image.value,
            gif_id: gif.value,
            yt_id: yt.value?.id,
            content: content.value
        });

        if (!props.item) {
            init();
        }
    }

    defineExpose({ focus });

    watch(() => props.item?.modify_time, init);

    watch(replyInput, init);

    watch(() => smiley.value?.opened, () => {
        if (smiley.value.opened) {
            const left = Math.min(props.item ? (props.item.parent_id ? 72 : 40) : 24, width.value - 330);
            const offset = props.fixed || useParentElement(smileyButton).value.offsetTop > 510 ? "-100%" : "32px";

            smileyClass.value = { [`left:${left}`]: true, [`translateY(${offset})`]: true };
        }
    });
</script>
